<template>
  <v-container>
    <v-row>
      <v-alert type="info" v-show="message">{{ message }}</v-alert>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            <v-avatar :color="child.color" size="36" class="ma-2">
              <span class="white--text">
                <strong>{{ child.chipInitials }}</strong>
              </span>
            </v-avatar>
            {{ child.childFirstName }} {{ child.childLastName }} Exam History
            <v-spacer></v-spacer>
            <AccountCreditChip />
            <ReturnHomeBtn />
            <v-btn
              class="ml-2 my-2 mr-2"
              color="primary"
              dark
              :to="{ name: 'ExamRegistration', params: { childId: childId } }"
              v-if="schoolYear || isAdmin"
            >
              <v-icon left>mdi-calendar-check</v-icon>Register for Exam
            </v-btn>

            <v-dialog v-model="refundDialog" max-width="400">
              <v-card>
                <v-card-title>Refund Exam Registration</v-card-title>
                <v-card-text>
                  <v-container>
                    <v-row>The amount for this exam registration is ${{ creditRecord.feeAmount}}.</v-row>
                    <v-row>
                      <v-text-field
                        type="number"
                        prefix="$"
                        v-model="refundAmount"
                        label="Refund Amount"
                      ></v-text-field>
                    </v-row>
                    <v-row>
                      <v-text-field
                        type="number"
                        prefix="$"
                        v-model="confirmAmount"
                        label="Confirm Refund Amount"
                        hint="Confirm refund by entering the Refund Amount."
                        persistent-hint
                      ></v-text-field>
                    </v-row>
                    <br />
                    <v-row>
                      <v-textarea
                        counter
                        outlined
                        label="Notes"
                        hint="Reason for the refund"
                        :rules="rules"
                        v-model="refundNotes"
                      ></v-textarea>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="refundDialog = false">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    @click="processRefund()"
                    :disabled="refundAmount != confirmAmount || refundAmount == 0"
                  >Refund</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-title>
          <v-data-table
            v-if="child"
            :headers="computedHeaders"
            :items="child.examRegistrations"
            :expanded.sync="expanded"
            show-expand
          >
            <template v-slot:top>
              <v-toolbar-title>
                <ExamRegistrationDialog
                  v-if="dialog"
                  v-bind:edited-item="editedItem"
                  v-bind:child-info="child"
                  v-on:save="save"
                  v-on:close="close"
                />
              </v-toolbar-title>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                Fees Paid? {{ item.isPaid ? "Yes" : "No" }}
                <br />
                Theoretical Exam Location: {{ item.theoryLocation }}
                <br />
                Practical Exam Location: {{ item.practicalLocation }}
                <br />
                Oral Exam Location: {{ item.oralLocation }}

              <template v-if="isAdmin || (!isAdmin && item.isFinal && canShowFinalGrades(item.schoolYearId))">
                <br />
                Final Marks: {{ item.totalMark }} 
                <br />
                Practical: {{ item.totalPracticalOralMark }} 
                <br />
                Theory: {{ item.totalTheoreticalMark }}
              </template>                
              </td>
            </template>
            <template
              v-slot:item.subjectId="{ item }"
            >{{ subjects.find(({ id }) => id === item.subjectId).description }}</template>
            <template v-slot:item.teacherId="{ item }">
              {{ teachers.find(({ id }) => id === item.teacherId).firstName }}
              {{ teachers.find(({ id }) => id === item.teacherId).lastName }}
            </template>
            <template
              v-slot:item.createdDate="{ item }"
            >{{ item.createdDate | formatDate }}</template>
            <template
              v-slot:item.practicalExamDate="{ item }"
            >{{ item.practicalExamDate | formatDateTime }}</template>
            <template
              v-slot:item.theoreticalExamDate="{ item }"
            >{{ item.theoreticalExamDate | formatDateTime }}</template>
            <template
              v-slot:item.oralExamDate="{ item }"
            >{{ item.oralExamDate | formatDateTime }}</template>

            <template v-slot:item.examGradeType="{ item }">
              <v-chip
                v-if="isAdmin || (!isAdmin && item.isFinal && canShowFinalGrades(item.schoolYearId))"
                :color="item.isFinal ? 'primary' : 'secondary'"
              >{{ examGradeType[item.examGradeType] }}</v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-layout :justify-end="true">
                <v-icon
                  class="mr-2"
                  v-if="item.examGradeType == 0 && isAdmin && item.isPaid"
                  @click.stop="refundItem(item)"
                >mdi-cash-usd-outline</v-icon>

                <v-btn
                  v-if="item.examGradeType == 0 && !item.isPaid"
                  text
                  icon
                  class="mr-1"
                  :to="{ name: 'CheckOut' }"
                >
                  <v-icon>mdi-cart</v-icon>
                </v-btn>

                <v-icon class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>

                <v-icon v-if="isAdmin && item.examGradeType == 0 && item.isPaid" @click="deleteItem(item)">mdi-delete</v-icon>

              </v-layout>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { errorMessageMixin, examGradeTypeMixin } from "../shared";
import ReturnHomeBtn from "../components/ReturnHomeBtn.vue";
import ExamRegistrationDialog from "../components/ExamRegistrationDialog.vue";
import AccountCreditChip from "../components/AccountCreditChip.vue";

export default {
  name: "History",
  props: {
    childId: {
      type: Number,
      default: 0,
      required: true
    }
  },
  mixins: [errorMessageMixin, examGradeTypeMixin],
  async created() {
    console.log("at created childid is ", this.childId);
    console.log(this.child.examRegistrations);
    this.$store.state.fullScreenLoading = false;
    if (!this.schoolYears || this.schoolYears.length === 0) {
      await this.loadSchoolYears();
    }
  },
  components: { ReturnHomeBtn, ExamRegistrationDialog, AccountCreditChip },
  data() {
    return {
      message: "",
      headers: [
        {
          text: "Created Date",
          value: "createdDate",
          align: "start"
        },
        {
          text: "Subject",
          value: "subjectId"
        },
        {
          text: "Grade Level",
          value: "gradeLevel"
        },
        {
          text: "Teacher",
          value: "teacherId"
        },
        {
          text: "Theoretical Exam Date",
          value: "theoreticalExamDate"
        },
        {
          text: "Practical Exam Date",
          value: "practicalExamDate"
        },
        {
          text: "Oral Exam Date",
          value: "oralExamDate"
        },
        {
          text: "Exam Grade",
          value: "examGradeType"
        },
        { text: "", value: "data-table-expand" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "end"
        }
      ],
      expanded: [],
      editedItem: {
        refundAmount: 0,
        confirmAmount: 0
      },
      dialog: false,
      refundDialog: false,
      creditRecord: {},
      refundAmount: 0,
      confirmAmount: 0,
      refundNotes: "",
      rules: [v => v.length <= 500 || "Max 500 characters"]
    };
  },
  computed: {
    ...mapState([
      "user",
      "account",
      "subjects",
      "teachers",
      "examGradeType",
      "schoolYear",
      "schoolYears"
    ]),
    ...mapGetters(["isAdmin"]),
    child: function() {
      return this.$store.state.account.children.find(
        ({ id }) => id === this.childId
      );
    },
    computedHeaders() {
      console.log("this.isAdmin", this.isAdmin);
      return this.headers.filter(x => this.isAdmin || x.text !== "Actions");
    }
  },
  methods: {
    ...mapActions([
      "logout",
      "getSchoolYearsAction",
      "updateExamRegistrationAction",
      "deleteExamRegistrationAction",
      "refundExamRegistrationAction"
    ]),
    async save() {
      try {
        const model = this.createExamRegistrationPatchModel();
        model.accountId = this.$store.state.account.id;

        await this.updateExamRegistrationAction({
          model: model,
          token: this.$store.state.token
        });
        this.close();
      } catch (err) {
        this.$store.state.error = this.errorMessage(err);
        this.$store.state.snackbar = true;
        console.log(err);
      }
    },
    async loadSchoolYears() {
      try {
        await this.getSchoolYearsAction();
        if (this.$store.state.schoolYears == null) {
          this.logout();
          console.log("im logged out");
          this.$router.go();
        }
      } catch (err) {
        this.$store.state.error = this.errorMessage(err);
        this.$store.state.snackbar = true;
        console.log(err);
      }
    },
    refundItem(item) {
      console.log("item to refund", item);

      this.creditRecord.accountId = this.account.id;
      this.creditRecord.adminId = this.user.id;
      this.creditRecord.adminName = this.user.firstName;
      this.creditRecord.examRegistrationId = item.id;
      this.creditRecord.childId = item.childId;
      this.creditRecord.feeAmount = item.feeAmount;

      this.feeAmount = item.feeAmount;
      this.refundAmount = parseInt(item.feeAmount);
      this.confirmAmount = 0;
      this.refundNotes = "Refund for grade level " + item.gradeLevel;
      this.refundDialog = true;
    },
    async processRefund() {
      this.$store.state.fullScreenLoading = true;

      this.creditRecord.amount = this.refundAmount;
      this.creditRecord.notes = this.refundNotes;

      console.log("refundModel", this.creditRecord);
      try {
        await this.refundExamRegistrationAction({
          model: this.creditRecord,
          token: this.$store.state.token
        });
        // write a message
        this.$store.state.error =
          "Exam Registration refunded for $" + this.creditRecord.amount;
        this.$store.state.snackbarColor = "success";
        this.$store.state.snackbar = true;
        console.log("delete done");
      } catch (err) {
        console.log("im in error");
        this.$store.state.error = this.errorMessage(err);
        this.$store.state.snackbar = true;
        console.log(err);
      }
      console.log("after the try");
      this.refundDialog = false;
      this.$store.state.fullScreenLoading = false;
    },

    async deleteItem(item) {
      if (confirm("Remove Exam Registration from cart?")) {
        this.$store.state.fullScreenLoading = true;
        const deleteExamRegistrationModel = {
          accountId: this.account.id,
          childId: item.childId,
          examRegistrationId: item.id
        };
        try {
          await this.deleteExamRegistrationAction({
            model: deleteExamRegistrationModel,
            token: this.$store.state.token
          });
          // write a message
          console.log("delete done");
        } catch (err) {
          this.$store.state.error = this.errorMessage(err);
          this.$store.state.snackbar = true;
          console.log(err);
        }
        this.$store.state.fullScreenLoading = false;
      }
    },

    canShowFinalGrades(schoolYearId) {
      const today = this.dateToString(new Date());
      if (!this.schoolYears) {
        return false;
      }
      const schoolYear = this.schoolYears.find(s => s.id === schoolYearId);
      if (!schoolYear) {
        return false;
      }
      return today > schoolYear.finalGradeReleaseDate;
    }
  }
};
</script>
